import { STORE, del, get, getAll, getAllByIndex, put, tx } from './db';
import type { SystemId } from '../emulator/types';
import {
  DEFAULT_PSX_MEMCARD_ID,
  SHARED_PSX_SAVE_KEY,
  psxMemcardSaveKey,
} from './psxMemcard';

export type SyncState = 'local' | 'synced' | 'pending' | 'conflict';

export {
  SHARED_PSX_SAVE_KEY,
  SHARED_PSX_SAVE_SHA1,
  DEFAULT_PSX_MEMCARD_ID,
  MAX_PSX_MEMCARDS,
  DEFAULT_PSX_MEMCARDS,
  PSX_MEMCARD_BYTES,
  allPsxMemcardBindings,
  isPsxMemcardSaveKey,
  psxMemcardSaveKey,
  psxMemcardSha1,
  psxMemcardSha1FromSaveKey,
  psxMemcardIdFromSaveKey,
  nextPsxMemcardId,
  createBlankPsxMemcard,
  isFormattedPsxMemcard,
  isPristineBlankPsxMemcard,
  normalizePsxMemcardForBoot,
  inspectPsxMemcard,
  formatPsxMemcardUsage,
  listPsxMemcardSaves,
  deletePsxMemcardSave,
  repairPsxMemcard,
  isPsxMemcardConsistent,
  PSX_MEMCARD_DATA_BLOCKS,
  type PsxMemcardMeta,
  type PsxMemcardUsage,
  type PsxMemcardSave,
  type PsxMemcardRepairResult,
} from './psxMemcard';

/** バッテリーバックアップ (SRAM)。ROM 1 本につき 1 レコード。 */
export interface SaveRecord {
  romId: string;
  data: ArrayBuffer;
  updatedAt: number;
  /** サーバー同期の状態 */
  syncState: SyncState;
  /** サーバ側 ETag。競合検出に使う。 */
  etag?: string;
  /** 最後にサーバーへ送った時刻 */
  syncedAt?: number;
}

/** セーブステート（任意の時点のスナップショット）。スロット制 + 自動保存。 */
export interface StateRecord {
  id: string;
  romId: string;
  /** 0-9 = 手動スロット、-1 = オートセーブ */
  slot: number;
  data: ArrayBuffer;
  /** サムネイル (data URL) */
  thumbnail?: string;
  createdAt: number;
  /** コアが変わるとステートは復元できないので記録しておく */
  backendId: string;
  syncState: SyncState;
  etag?: string;
}

export const AUTOSAVE_SLOT = -1;
export const MAX_MANUAL_SLOTS = 10;

/**
 * その機種のセーブをどのキーで保存するか。
 * PS1 はアクティブなメモリーカード（既定は共有カード 0）。
 */
export function sramKeyFor(
  system: SystemId,
  romId: string,
  psxMemcardId: string = DEFAULT_PSX_MEMCARD_ID,
): string {
  return system === 'psx' ? psxMemcardSaveKey(psxMemcardId) : romId;
}

/* -------------------------------------------------------------------------- */
/* SRAM                                                                        */
/* -------------------------------------------------------------------------- */

export async function saveSram(romId: string, data: Uint8Array): Promise<SaveRecord> {
  const existing = await get<SaveRecord>(STORE.saves, romId);

  // 中身が変わっていなければ書かない（同期の無駄打ちを防ぐ）
  if (existing && equalBytes(new Uint8Array(existing.data), data)) {
    return existing;
  }

  // etag は残す。消すと「前回同期済みの続き」が「未知のローカル新規」扱いになり、
  // サーバーに同名があると誤って競合になる。
  const record: SaveRecord = {
    romId,
    data: copyBuffer(data),
    updatedAt: Date.now(),
    syncState: 'pending',
    ...(existing?.etag !== undefined ? { etag: existing.etag } : {}),
    ...(existing?.syncedAt !== undefined ? { syncedAt: existing.syncedAt } : {}),
  };
  await put(STORE.saves, record);
  return record;
}

export async function loadSram(romId: string): Promise<Uint8Array | undefined> {
  const rec = await get<SaveRecord>(STORE.saves, romId);
  return rec ? new Uint8Array(rec.data) : undefined;
}

export async function deleteSram(romId: string): Promise<void> {
  await del(STORE.saves, romId);
}

export async function getSaveRecord(romId: string): Promise<SaveRecord | undefined> {
  return get<SaveRecord>(STORE.saves, romId);
}

export async function markSaveSynced(
  romId: string,
  etag: string,
  /** 指定時は世代が一致するときだけ synced にする（flush レース対策） */
  expectedUpdatedAt?: number,
): Promise<boolean> {
  const rec = await get<SaveRecord>(STORE.saves, romId);
  if (!rec) return false;
  if (expectedUpdatedAt !== undefined && rec.updatedAt !== expectedUpdatedAt) {
    // アップロード中にローカルが進んだ。etag だけ残して再送待ち。
    await put(STORE.saves, { ...rec, syncState: 'pending', etag } satisfies SaveRecord);
    return false;
  }
  await put(STORE.saves, { ...rec, syncState: 'synced', etag, syncedAt: Date.now() } satisfies SaveRecord);
  return true;
}

/** アップロードは成功したがローカルが進んでいるとき。etag だけ揃え、再送待ちにする。 */
export async function markSavePendingWithEtag(romId: string, etag: string): Promise<void> {
  const rec = await get<SaveRecord>(STORE.saves, romId);
  if (!rec) return;
  await put(STORE.saves, { ...rec, syncState: 'pending', etag } satisfies SaveRecord);
}

export async function listPendingSaves(): Promise<SaveRecord[]> {
  return getAllByIndex<SaveRecord>(STORE.saves, 'syncState', 'pending');
}

export async function listAllSaves(): Promise<SaveRecord[]> {
  return getAll<SaveRecord>(STORE.saves);
}

/**
 * サーバーから取り込んだ SRAM を反映する。
 * 取り込んだ直後は「同期済み」なので、次の同期で送り返さない。
 */
export async function applyRemoteSram(
  romId: string,
  data: Uint8Array,
  etag: string,
  updatedAt: number,
): Promise<void> {
  await put(STORE.saves, {
    romId,
    data: copyBuffer(data),
    updatedAt,
    syncState: 'synced',
    etag,
    syncedAt: Date.now(),
  } satisfies SaveRecord);
}

export async function markSaveConflicted(romId: string): Promise<void> {
  const rec = await get<SaveRecord>(STORE.saves, romId);
  if (!rec) return;
  await put(STORE.saves, { ...rec, syncState: 'conflict' } satisfies SaveRecord);
}

/* -------------------------------------------------------------------------- */
/* セーブステート                                                              */
/* -------------------------------------------------------------------------- */

export interface CreateStateOptions {
  romId: string;
  slot: number;
  data: Uint8Array;
  backendId: string;
  thumbnail?: string;
}

export async function saveState(opts: CreateStateOptions): Promise<StateRecord> {
  const id = `${opts.romId}:${opts.slot}`;
  const existing = await get<StateRecord>(STORE.states, id);

  // 中身が同じなら書かない（同期の無駄打ち・誤競合を防ぐ）
  if (
    existing &&
    existing.backendId === opts.backendId &&
    equalBytes(new Uint8Array(existing.data), opts.data)
  ) {
    if (opts.thumbnail && opts.thumbnail !== existing.thumbnail) {
      const withThumb: StateRecord = { ...existing, thumbnail: opts.thumbnail };
      await put(STORE.states, withThumb);
      return withThumb;
    }
    return existing;
  }

  const record: StateRecord = {
    // 同一スロットは上書きしたいので ID をスロット由来にする
    id,
    romId: opts.romId,
    slot: opts.slot,
    data: copyBuffer(opts.data),
    createdAt: Date.now(),
    backendId: opts.backendId,
    syncState: 'pending',
    // 前回サーバー同期時の ETag を残し、誤競合を防ぐ
    ...(existing?.etag !== undefined ? { etag: existing.etag } : {}),
    ...(opts.thumbnail ? { thumbnail: opts.thumbnail } : {}),
  };
  await put(STORE.states, record);
  return record;
}

export async function loadState(romId: string, slot: number): Promise<StateRecord | undefined> {
  return get<StateRecord>(STORE.states, `${romId}:${slot}`);
}

export async function listStates(romId: string): Promise<StateRecord[]> {
  const states = await getAllByIndex<StateRecord>(STORE.states, 'romId', romId);
  return states.sort((a, b) => a.slot - b.slot);
}

/** 一覧表示用にサムネイルとメタだけ返す（本体は読み込まない）。 */
export async function listStateSummaries(
  romId: string,
): Promise<Array<Omit<StateRecord, 'data'> & { size: number }>> {
  const states = await listStates(romId);
  return states.map(({ data, ...rest }) => ({ ...rest, size: data.byteLength }));
}

export async function deleteState(romId: string, slot: number): Promise<void> {
  await del(STORE.states, `${romId}:${slot}`);
}

export async function listAllStates(): Promise<StateRecord[]> {
  return getAll<StateRecord>(STORE.states);
}

export async function markStateSynced(romId: string, slot: number, etag: string): Promise<void> {
  const rec = await get<StateRecord>(STORE.states, `${romId}:${slot}`);
  if (!rec) return;
  await put(STORE.states, { ...rec, syncState: 'synced', etag } satisfies StateRecord);
}

export async function markStatePendingWithEtag(romId: string, slot: number, etag: string): Promise<void> {
  const rec = await get<StateRecord>(STORE.states, `${romId}:${slot}`);
  if (!rec) return;
  await put(STORE.states, { ...rec, syncState: 'pending', etag } satisfies StateRecord);
}

export async function markStateConflicted(romId: string, slot: number): Promise<void> {
  const rec = await get<StateRecord>(STORE.states, `${romId}:${slot}`);
  if (!rec) return;
  await put(STORE.states, { ...rec, syncState: 'conflict' } satisfies StateRecord);
}

/**
 * サーバーから取り込んだセーブステートを反映する。
 *
 * ステートはコアに強く依存するので、どのコアで作られたかを一緒に持ち回る。
 * 分からない場合は 'unknown' を入れておき、復元時に警告できるようにする。
 */
export async function applyRemoteState(
  romId: string,
  slot: number,
  data: Uint8Array,
  etag: string,
  createdAt: number,
  backendId = 'unknown',
): Promise<void> {
  await put(STORE.states, {
    id: `${romId}:${slot}`,
    romId,
    slot,
    data: copyBuffer(data),
    createdAt,
    backendId,
    syncState: 'synced',
    etag,
  } satisfies StateRecord);
}

/** 全 ROM のオートセーブを消す（設定画面の「容量を空ける」用） */
export async function purgeAutosaves(): Promise<number> {
  const all = await getAll<StateRecord>(STORE.states);
  const targets = all.filter((s) => s.slot === AUTOSAVE_SLOT);
  if (targets.length === 0) return 0;
  await tx(STORE.states, 'readwrite', (t) => {
    const store = t.objectStore(STORE.states);
    for (const s of targets) store.delete(s.id);
  });
  return targets.length;
}

export interface DeleteSavesOptions {
  /** SRAM（バッテリーバックアップ）を消す */
  sram?: boolean;
  /** セーブステートを消す */
  states?: boolean;
  /**
   * PS1 共有メモリーカードを消すか。
   * 未指定時は「この端末に他の PS1 ソフトが無いときだけ」消す。
   */
  sharedPsxCard?: boolean;
}

/**
 * 指定 ROM のローカルセーブを消す（ROM 本体は残す）。
 * @returns 削除した件数（SRAM 1 + ステート数）
 */
export async function deleteSavesForRom(
  romId: string,
  system: SystemId,
  opts: DeleteSavesOptions = {},
): Promise<number> {
  const wipeSram = opts.sram !== false;
  const wipeStates = opts.states !== false;
  let removed = 0;

  if (wipeStates) {
    const states = await listStates(romId);
    for (const s of states) {
      await del(STORE.states, s.id);
      removed++;
    }
  }

  if (wipeSram) {
    if (system === 'psx') {
      const wipeShared =
        opts.sharedPsxCard ??
        (await (async () => {
          const roms = await getAll<{ id: string; system: string }>(STORE.roms);
          return roms.filter((r) => r.system === 'psx' && r.id !== romId).length === 0;
        })());
      if (wipeShared) {
        const existing = await get<SaveRecord>(STORE.saves, SHARED_PSX_SAVE_KEY);
        if (existing) {
          await del(STORE.saves, SHARED_PSX_SAVE_KEY);
          removed++;
        }
      }
    } else {
      const existing = await get<SaveRecord>(STORE.saves, romId);
      if (existing) {
        await del(STORE.saves, romId);
        removed++;
      }
    }
  }

  return removed;
}

/* -------------------------------------------------------------------------- */

function copyBuffer(data: Uint8Array): ArrayBuffer {
  // subarray / WASM ヒープのビューを渡されても、ちょうど data 分だけを独立バッファにする
  const out = new ArrayBuffer(data.byteLength);
  new Uint8Array(out).set(data);
  return out;
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
